import { ReactNode, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExploreIcon from '@mui/icons-material/Explore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import AppsIcon from '@mui/icons-material/Apps';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShieldIcon from '@mui/icons-material/Shield';
import MemoryIcon from '@mui/icons-material/Memory';
import HubIcon from '@mui/icons-material/Hub';
import TerminalIcon from '@mui/icons-material/Terminal';
import ChatIcon from '@mui/icons-material/Chat';
import BiotechIcon from '@mui/icons-material/Biotech';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import MedicationIcon from '@mui/icons-material/Medication';
import DescriptionIcon from '@mui/icons-material/Description';
import ScienceIcon from '@mui/icons-material/Science';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InsightsIcon from '@mui/icons-material/Insights';
import CloudIcon from '@mui/icons-material/Cloud';
import BoltIcon from '@mui/icons-material/Bolt';
import GroupsIcon from '@mui/icons-material/Groups';
import RateReviewIcon from '@mui/icons-material/RateReview';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TimerIcon from '@mui/icons-material/Timer';
import { useUserContext } from '@/contexts/userContext';
import { Navigate, useNavigate } from 'react-router-dom';

const BIOPHARM_URL = 'https://www.biopharm.minotii.ai';

const DISCOVERY_STEPS = [
  'Literature Intelligence',
  'Target Identification',
  'IP & Novelty Screen',
  'Biological Plausibility',
  'Structure Retrieval',
  'Known Compounds',
  'ADMET Triage',
  'Validation Brief',
  'Virtual Screening',
  'Generative Chemistry',
];

const BIOPHARM_APPS = [
  { icon: <TravelExploreIcon />, title: 'Target Scout', tag: 'Steps 1–4', desc: 'Ranked, evidence-weighted target shortlist with druggability scores.' },
  { icon: <ViewInArIcon />, title: 'Structure Ready', tag: 'Steps 5–6', desc: 'PDB and AlphaFold structures, binding sites and a known-compound SAR summary.' },
  { icon: <MedicationIcon />, title: 'ADMET Screener', tag: 'Step 7', desc: 'Batch ADMET for up to 1,000 compounds, 41 properties each.' },
  { icon: <DescriptionIcon />, title: 'Target Dossier', tag: 'Steps 1–8', desc: 'Full evidence brief from disease input to validated target.' },
  { icon: <BiotechIcon />, title: 'Virtual Screener', tag: 'Steps 5, 9', desc: 'Dock up to 100K ZINC compounds, ranked with ADMET pre-filtering.' },
  { icon: <ScienceIcon />, title: 'Molecule Generator', tag: 'Step 10', desc: 'REINVENT 4 proposes novel scaffolds tuned for affinity and properties.' },
];

const CLINICAL_FEATURES = [
  { icon: <GroupsIcon />, title: 'Programs & signing roles', desc: 'Create a program from a discovery campaign and grant dated roles: Regulatory Lead, Medical Writer, Safety Physician, CMC Lead, Reviewer.' },
  { icon: <RateReviewIcon />, title: 'Review workbench', desc: 'Versioned controlled documents, reviewed section by section with sources and AI credibility records alongside.' },
  { icon: <VerifiedUserIcon />, title: '21 CFR Part 11 e-signatures', desc: 'Re-authenticated, append-only signatures bound to a hash of the exact content.' },
  { icon: <TimerIcon />, title: 'Regulatory clocks', desc: '7- and 15-day IND safety reports, the 30-day IND review and FDA information requests, in one approvals inbox.' },
];

// Shared section heading: small label chip, headline with an emphasised tail, optional intro
const SectionHeading = ({
  label,
  title,
  emphasis,
  intro,
  align = 'left',
  dark = false,
}: {
  label: string;
  title: ReactNode;
  emphasis?: ReactNode;
  intro?: ReactNode;
  align?: 'left' | 'center';
  dark?: boolean;
}) => (
  <Box sx={{ textAlign: align, mb: 5, maxWidth: align === 'center' ? 720 : 'none', mx: align === 'center' ? 'auto' : 0 }}>
    <Typography
      variant="overline"
      sx={{
        display: 'inline-block',
        px: 1.5,
        py: 0.25,
        mb: 2,
        borderRadius: 999,
        fontWeight: 700,
        letterSpacing: 1.2,
        lineHeight: 2,
        color: dark ? 'secondary.main' : 'primary.main',
        bgcolor: dark ? 'rgba(139,216,188,0.12)' : 'rgba(139,216,188,0.25)',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h2"
      sx={{ fontWeight: 800, mb: 2, lineHeight: 1.12, letterSpacing: '-0.02em', fontSize: { xs: '2rem', md: '2.75rem' }, color: dark ? 'white' : 'primary.main' }}
    >
      {title}
      {emphasis && (
        <Box component="span" sx={{ color: dark ? 'secondary.main' : 'secondary.dark' }}>
          {' '}
          {emphasis}
        </Box>
      )}
    </Typography>
    {intro && (
      <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7, color: dark ? 'rgba(255,255,255,0.78)' : 'text.secondary' }}>
        {intro}
      </Typography>
    )}
  </Box>
);

const CheckList = ({ items }: { items: string[] }) => (
  <List disablePadding>
    {items.map((item) => (
      <ListItem key={item} sx={{ px: 0, py: 0.5, alignItems: 'flex-start' }}>
        <ListItemIcon sx={{ minWidth: 34, mt: 0.25 }}>
          <CheckCircleOutlineIcon sx={{ color: 'secondary.dark', fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText primary={item} primaryTypographyProps={{ fontWeight: 500, color: 'text.primary', fontSize: '0.95rem' }} />
      </ListItem>
    ))}
  </List>
);

const ShowcaseImage = ({ src, alt }: { src: string; alt: string }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 5,
      bgcolor: 'white',
      border: '1px solid rgba(35,53,101,0.08)',
      boxShadow: '0 24px 60px rgba(35,53,101,0.12)',
      overflow: 'hidden',
    }}
  >
    <img src={src} alt={alt} style={{ width: '100%', borderRadius: '14px', display: 'block' }} />
  </Box>
);

const Home = () => {
  const userContext = useUserContext();
  const navigate = useNavigate();
  const theme = useTheme();

  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    if (userContext?.user) {
      setSignedIn(true);
    }
  }, [userContext?.user]);

  const getStarted = () => {
    navigate('/login');
  };

  if (signedIn) {
    return <Navigate to={'/dashboard'} />;
  }

  const cardSx = {
    borderRadius: 4,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    height: '100%',
    transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.3),
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
      transform: 'translateY(-2px)',
    },
  };

  return (
    <Box sx={{ bgcolor: 'background.default', overflow: 'hidden' }}>

      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(160deg, ${theme.palette.primary.main} 0%, #101c3a 100%)`,
          color: 'white',
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: '-15%', right: '-10%', width: '55%', height: '70%', opacity: 0.3, filter: 'blur(90px)', bgcolor: 'secondary.main', borderRadius: '50%' }} />
        <Box sx={{ position: 'absolute', bottom: '-25%', left: '-10%', width: '45%', height: '55%', opacity: 0.12, filter: 'blur(70px)', bgcolor: '#38bdf8', borderRadius: '50%' }} />

        <Grid container spacing={6} sx={{ maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }} className="animate-fade-in">
            <Chip
              icon={<BiotechIcon sx={{ fontSize: 16 }} />}
              label="New · BioPharm Suite & Clinical Programs"
              size="small"
              component="a"
              href={BIOPHARM_URL}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              sx={{ bgcolor: 'rgba(139,216,188,0.15)', color: 'secondary.main', border: '1px solid rgba(139,216,188,0.4)', fontWeight: 600, mb: 3, borderRadius: 999, '& .MuiChip-icon': { color: 'secondary.main' } }}
            />
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.6rem', md: '3.6rem' }, lineHeight: 1.08, fontWeight: 800, letterSpacing: '-0.03em', mb: 1.5 }}
            >
              Agentic AI pipelines for every industry.
            </Typography>
            <Typography sx={{ fontFamily: 'Alkatra', fontSize: { xs: '1.3rem', md: '1.6rem' }, color: 'secondary.main', mb: 3 }}>
              Any Data. Any Format. <i>Instant Intelligence.</i>
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.85, maxWidth: 560, fontSize: '1.1rem', lineHeight: 1.7 }}>
              Connect documents, streams and scientific data to Java and Python agents, a conversational copilot and any major LLM, then run it all as governed, traceable pipelines.
            </Typography>

            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 4 }}>
              {[
                { icon: <ChatIcon sx={{ fontSize: 14 }} />, label: 'AI Chat Copilot' },
                { icon: <BiotechIcon sx={{ fontSize: 14 }} />, label: 'Drug Discovery' },
                { icon: <HubIcon sx={{ fontSize: 14 }} />, label: 'Event-Driven Bus' },
                { icon: <MemoryIcon sx={{ fontSize: 14 }} />, label: 'Java · Python Agents' },
                { icon: <TerminalIcon sx={{ fontSize: 14 }} />, label: 'MCP Native' },
                { icon: <ShieldIcon sx={{ fontSize: 14 }} />, label: 'Tenant-Isolated RBAC' },
              ].map((p) => (
                <Chip
                  key={p.label}
                  icon={p.icon}
                  label={p.label}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 999, '& .MuiChip-icon': { color: 'secondary.main' } }}
                />
              ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                size="large"
                onClick={getStarted}
                endIcon={<ArrowForwardIcon />}
                sx={{ bgcolor: 'secondary.main', color: 'primary.main', fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: 'white' } }}
              >
                Launch Console
              </Button>
              <Button
                variant="outlined"
                size="large"
                href={BIOPHARM_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.35)', fontWeight: 600, px: 3.5, py: 1.5, borderRadius: 3, textTransform: 'none', fontSize: '1rem', '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'rgba(139,216,188,0.06)' } }}
              >
                Open BioPharm
              </Button>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              className="glass-card animate-fade-in"
              sx={{ p: 1.5, borderRadius: 5, transform: 'perspective(1200px) rotateY(-8deg) rotateX(3deg)', boxShadow: '0 40px 80px -20px rgba(0,0,0,0.6)' }}
            >
              <img src="landing_hero_visual.png" alt="Minotii platform console" style={{ width: '100%', borderRadius: '14px', display: 'block' }} />
            </Box>
          </Grid>
        </Grid>

        {/* Platform stats */}
        <Grid container sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, mt: { xs: 6, md: 10 }, position: 'relative', zIndex: 1 }}>
          {[
            { icon: <AppsIcon />, label: 'Tenant app isolation', value: 'Multi-Tenant' },
            { icon: <StorageIcon />, label: 'Ingestion engine', value: 'Multi-Modal & IoT' },
            { icon: <SmartToyIcon />, label: 'Agent runtimes', value: 'Java · Python · MCP' },
            { icon: <AccountTreeIcon />, label: 'Pipeline engine', value: 'Declarative DAG' },
          ].map((stat) => (
            <Grid size={{ xs: 6, md: 3 }} key={stat.label} sx={{ textAlign: 'center', py: 2.5, borderLeft: { md: '1px solid rgba(255,255,255,0.08)' }, '&:first-of-type': { borderLeft: 'none' } }}>
              <Box sx={{ color: 'secondary.main', mb: 0.5 }}>{stat.icon}</Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{stat.value}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{stat.label}</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ─── ARCHITECTURE ─────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 4 } }}>
        <SectionHeading
          align="center"
          label="Architecture"
          title="Five tiers,"
          emphasis="one platform."
          intro="Built for high-throughput ingestion, asynchronous event orchestration, multi-runtime agents and multi-cloud deployment."
        />
        <Grid container spacing={2}>
          {[
            { num: '01', title: 'Multi-modal ingestion', desc: 'PDFs, OCR, RTSP video, OPC-UA and Kafka streams, FASTA/PDB sequences, Excel and SQL.', badge: 'Input' },
            { num: '02', title: 'Event backbone', desc: 'Pub/sub topic isolation, payload deduplication and dead-letter queues.', badge: 'Events' },
            { num: '03', title: 'Multi-runtime agents', desc: 'Java (Spring) and Python agents bound through the Model Context Protocol.', badge: 'Execution' },
            { num: '04', title: 'LLM gateway & RAG', desc: 'Azure OpenAI, Gemini, Claude or local vLLM, with pgvector retrieval.', badge: 'Intelligence' },
            { num: '05', title: 'Security & tracing', desc: 'OAuth2/OIDC, JWT, CMEK, OpenTelemetry and end-to-end run tracing.', badge: 'Security' },
          ].map((layer) => (
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={layer.num}>
              <Card variant="outlined" sx={cardSx}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography sx={{ color: 'secondary.dark', fontWeight: 800, fontFamily: 'monospace', fontSize: '1.1rem' }}>{layer.num}</Typography>
                    <Chip label={layer.badge} size="small" sx={{ fontSize: '0.65rem', fontWeight: 600, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.06) }} />
                  </Stack>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75, color: 'primary.main', lineHeight: 1.3 }}>{layer.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{layer.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ─── BIOPHARM SUITE & CLINICAL PROGRAMS ───────────────────── */}
      <Box sx={{ background: `linear-gradient(180deg, #0b1630 0%, ${theme.palette.primary.main} 100%)`, color: 'white', py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 4 } }}>
          <Grid container spacing={6} alignItems="flex-end" sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <SectionHeading
                dark
                label="Minotii BioPharm"
                title="From disease hypothesis"
                emphasis="to clinical program."
                intro="Ten composable agents take a team from a disease hypothesis to a validated target and lead compounds, autonomously or with scientist approval gates, then into governed clinical programs."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: { md: 'flex-end' }, mb: 5 }}>
              <Button
                variant="contained"
                href={BIOPHARM_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                sx={{ bgcolor: 'secondary.main', color: 'primary.main', fontWeight: 700, px: 3.5, py: 1.4, borderRadius: 3, textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: 'white' } }}
              >
                Open Minotii BioPharm
              </Button>
            </Grid>
          </Grid>

          {/* 10-step discovery pipeline */}
          <Grid container spacing={1.5} sx={{ mb: 6 }}>
            {DISCOVERY_STEPS.map((step, i) => (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={step}>
                <Box sx={{ p: 1.75, height: '100%', borderRadius: 3, border: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.04)' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'secondary.main', fontWeight: 700 }}>{String(i + 1).padStart(2, '0')}</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', lineHeight: 1.3 }}>{step}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Six apps */}
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5 }}>Six apps, ready to run</Typography>
          <Grid container spacing={2} sx={{ mb: 8 }}>
            {BIOPHARM_APPS.map((app) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={app.title}>
                <Box sx={{ p: 2.5, height: '100%', borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', transition: 'background 0.2s', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(139,216,188,0.15)', color: 'secondary.main' }}>
                      {app.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>{app.title}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'secondary.main', fontFamily: 'monospace' }}>{app.tag}</Typography>
                    </Box>
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>{app.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Clinical programs */}
          <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 5, bgcolor: 'white', color: 'text.primary', boxShadow: '0 30px 70px rgba(0,0,0,0.3)' }}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Chip label="Clinical Programs · Early access" size="small" sx={{ fontWeight: 700, mb: 2, borderRadius: 999, bgcolor: alpha(theme.palette.secondary.main, 0.25), color: 'primary.main' }} />
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', mb: 1.5, letterSpacing: '-0.02em' }}>
                  Carry a GO dossier into the clinic.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2.5 }}>
                  A clinical program holds the IND, protocols, regulatory documents and FDA deadlines for a compound, with signing authority built in. Every AI-drafted section carries its sources and a credibility assessment.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {['IND Package Builder', 'Protocol Designer', 'Regulatory Authoring', 'Safety Monitor', 'FDA Correspondence', 'eCTD Assembler'].map((s, i) => (
                    <Chip key={s} label={`Stage ${11 + i} · ${s}`} size="small" variant="outlined" sx={{ borderRadius: 999, borderStyle: 'dashed', fontSize: '0.72rem' }} />
                  ))}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <Grid container spacing={2}>
                  {CLINICAL_FEATURES.map((f) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={f.title}>
                      <Box sx={{ p: 2.25, height: '100%', borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}` }}>
                        <Box sx={{ color: 'secondary.dark', mb: 0.75 }}>{f.icon}</Box>
                        <Typography sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>{f.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{f.desc}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* ─── PIPELINE BUILDER ─────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 4 } }}>
        <Grid container spacing={8} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <ShowcaseImage src="pipeline_builder_visual.png" alt="Visual pipeline builder" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionHeading
              label="Pipeline builder"
              title="Build AI pipelines visually,"
              emphasis="run them as a declarative graph."
              intro="Drag and drop multi-agent workflows: connect data streams, route through LLMs and vision models, and trigger downstream enterprise APIs."
            />
            <CheckList
              items={[
                'DAG execution with automatic node retry and fallback routing',
                'One LLM gateway for Azure OpenAI, Gemini, Claude or local vLLM',
                'Chain agents across Java, Python and Node.js runtimes',
                'Clone and extend versioned Marketplace blueprints',
              ]}
            />
            <Button variant="contained" color="primary" onClick={getStarted} endIcon={<ArrowForwardIcon />} sx={{ mt: 3, borderRadius: 3, fontWeight: 700, px: 3, textTransform: 'none' }}>
              Open the canvas
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ─── CHAT COPILOT ─────────────────────────────────────────── */}
      <Box sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 4 } }}>
          <Grid container spacing={8} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionHeading
                label="Chat copilot"
                title="Orchestrate workflows"
                emphasis="in plain language."
                intro="Ask the copilot to find data sources, assemble multi-agent pipelines, inspect intermediate steps and preview live entity context."
              />
              <CheckList
                items={[
                  'Author, tune and test pipelines from a conversation',
                  'Step-level inspector with reasoning traces and outputs',
                  'Context previews for entities, data sources and agents',
                  'Direct access to the MCP tool registry',
                ]}
              />
              <Button variant="contained" color="primary" onClick={getStarted} endIcon={<ArrowForwardIcon />} sx={{ mt: 3, borderRadius: 3, fontWeight: 700, px: 3, textTransform: 'none' }}>
                Try the copilot
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ShowcaseImage src="event_driven_intelligence_visual.png" alt="Agents collaborating over the event bus" />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ─── OBSERVABILITY ────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 4 } }}>
        <Grid container spacing={8} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <ShowcaseImage src="monitoring.png" alt="Access control, usage statistics and audit trail" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionHeading
              label="Observability"
              title="One command center"
              emphasis="for every run."
              intro="Trace each pipeline run end to end and see agents, data streams, LLM usage and audit history in one place."
            />
            <Grid container spacing={2}>
              {[
                { icon: <AccountTreeIcon />, title: 'Run tracing', desc: 'Every run traced by pipeline instance, with node latency and failover status.' },
                { icon: <SmartToyIcon />, title: 'Agent health', desc: 'Java and Python agents monitored with confidence metrics.' },
                { icon: <StorageIcon />, title: 'Ingestion health', desc: 'Throughput, error rates and schema checks for every stream.' },
                { icon: <InsightsIcon />, title: 'Token & cost analytics', desc: 'Per-pipeline token use, model spend and audit trail.' },
              ].map((f) => (
                <Grid size={{ xs: 12, sm: 6 }} key={f.title}>
                  <Card variant="outlined" sx={cardSx}>
                    <CardContent sx={{ p: 2.25 }}>
                      <Box sx={{ color: 'secondary.dark', mb: 0.5 }}>{f.icon}</Box>
                      <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{f.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>{f.desc}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* ─── MARKETPLACE ──────────────────────────────────────────── */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1320, mx: 'auto', px: { xs: 2, md: 4 } }}>
          <Grid container spacing={8} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionHeading
                label="Blueprint marketplace"
                title="Discover, deploy"
                emphasis="and publish versioned blueprints."
                intro="Containerised agent blueprints for life sciences, insurance, agritech, manufacturing and legal compliance."
              />
              <CheckList
                items={[
                  'Industry blueprints you can deploy as-is or extend',
                  'Templates for drug discovery, 3D molecular analysis and claims',
                  'One-click provisioning into your private tenant',
                  'Semantic versioning and an audit trail per blueprint',
                ]}
              />
              <Button variant="contained" color="primary" onClick={getStarted} endIcon={<ExploreIcon />} sx={{ mt: 3, borderRadius: 3, fontWeight: 700, px: 3, textTransform: 'none' }}>
                Explore blueprints
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ShowcaseImage src="marketplace_visual.png" alt="Minotii blueprint marketplace" />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ─── SECURITY & GOVERNANCE ────────────────────────────────── */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 } }}>
          <SectionHeading
            dark
            align="center"
            label="Security & governance"
            title="Track everything."
            emphasis="Scale with zero trust."
            intro="Tenant boundaries, per-user access to every entity, exportable audit trails and native MCP integration."
          />
          <Grid container spacing={2.5}>
            {[
              { icon: <ApartmentIcon />, title: 'Multi-tenant isolation', desc: 'Tenant-bound data plane, encrypted at rest and in transit.' },
              { icon: <LockPersonIcon />, title: 'Granular access control', desc: 'Per-user access to pipelines, agents and programs, with instant token revocation.' },
              { icon: <FactCheckIcon />, title: 'Auditable tool execution', desc: 'Every LLM query and agent tool call logged with user and confidence.' },
              { icon: <DashboardIcon />, title: 'OpenTelemetry analytics', desc: 'Model latency, token use, error rates and spend in real time.' },
              { icon: <CloudIcon />, title: 'Deploy anywhere', desc: 'Managed SaaS, dedicated cloud VPC or air-gapped edge, on one topology.' },
              { icon: <BoltIcon />, title: 'Native MCP server', desc: 'Connect your data and tools to Claude Desktop and AI IDEs.' },
            ].map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Box sx={{ p: 3, height: '100%', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.04)', transition: 'background 0.2s', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}>
                  <Box sx={{ color: 'secondary.main', mb: 1.5 }}>{f.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75, fontSize: '1.1rem' }}>{f.title}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.78, lineHeight: 1.7 }}>{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ─── DEPLOYMENT OPTIONS ───────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 } }}>
        <SectionHeading
          align="center"
          label="Deployment"
          title="Deploy anywhere,"
          emphasis="any way."
          intro="From zero-ops SaaS to air-gapped hybrid edge, Minotii fits your security mandates."
        />
        <Grid container spacing={2.5}>
          {[
            { title: 'Managed cloud SaaS', desc: 'No infrastructure to run, automatic scaling, tenant-isolated.', badge: 'Fastest start' },
            { title: 'Dedicated private VPC', desc: 'Inside your AWS, Azure or GCP account with customer-managed keys. Data stays local.', badge: 'Highest compliance' },
            { title: 'Hybrid edge / on-premise', desc: 'Local processing for sensitive streams on Kubernetes, Docker Compose or NVIDIA Jetson.', badge: 'Most flexible' },
          ].map((item) => (
            <Grid size={{ xs: 12, md: 4 }} key={item.title}>
              <Card variant="outlined" sx={cardSx}>
                <CardContent sx={{ p: 3 }}>
                  <Chip label={item.badge} size="small" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.2), color: 'primary.main', fontWeight: 700, fontSize: '0.7rem', mb: 2, borderRadius: 999 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{item.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ─── CTA ──────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 8, md: 12 } }}>
        <Box
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            textAlign: 'center',
            py: { xs: 6, md: 8 },
            px: 3,
            borderRadius: 6,
            color: 'white',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #0b1630 100%)`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'absolute', top: '-40%', right: '-10%', width: '50%', height: '120%', opacity: 0.25, filter: 'blur(80px)', bgcolor: 'secondary.main', borderRadius: '50%' }} />
          <Box sx={{ position: 'relative' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5, letterSpacing: '-0.02em', fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Ready to deploy agentic pipelines?
            </Typography>
            <Typography sx={{ opacity: 0.8, mb: 4, maxWidth: 520, mx: 'auto', fontSize: '1.1rem' }}>
              Launch your first pipeline on Minotii in minutes.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={getStarted}
                endIcon={<ArrowForwardIcon />}
                sx={{ bgcolor: 'secondary.main', color: 'primary.main', px: 4, py: 1.5, borderRadius: 3, fontWeight: 700, textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: 'white' } }}
              >
                Access the console
              </Button>
              <Button
                variant="outlined"
                size="large"
                href={BIOPHARM_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.35)', px: 3.5, py: 1.5, borderRadius: 3, fontWeight: 600, textTransform: 'none', fontSize: '1rem', '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' } }}
              >
                Explore BioPharm
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
